/**
 * @file BadgeImage.jsx
 * @description Reusable badge renderer used across the Dashboard, Profile, and notification modals.
 *              Resolves the best available image source (DB path -> ID-based fallback -> emoji icon)
 *              and applies earned/locked visual treatment based on the user's achievement state.
 *              Handles image loading states with a skeleton shimmer and graceful error fallbacks.
 *
 * @module components/BadgeImage
 */
import React, { useState } from 'react';
import { FiAward, FiLock } from 'react-icons/fi';

/* ══════════════════════════════════════════════════════════════
 * SIZE CONFIGURATION
 * ══════════════════════════════════════════════════════════════ */

/**
 * Tailwind dimension classes keyed by the `size` prop.
 * Used for both the container and the rendered <img> element.
 */
const SIZE_CLASSES = {
    xs: 'w-8 h-8',
    sm: 'w-12 h-12',
    md: 'w-16 h-16',
    lg: 'w-24 h-24',
    xl: 'w-32 h-32'
};

/* ══════════════════════════════════════════════════════════════
 * HELPERS
 * ══════════════════════════════════════════════════════════════ */

/**
 * Returns contextual tooltip text based on the badge's earned/locked state.
 * Priority: earned description -> locked description -> generic fallback.
 *
 * @param {boolean} isEarned - Whether the user has unlocked this badge.
 * @param {string|undefined} earnedDescription - Text shown when badge is earned.
 * @param {string|undefined} lockedDescription - Text shown when badge is locked.
 * @param {string} fallbackDescription - Last-resort description string.
 * @param {string} badgeTitle - Badge display name (ultimate fallback).
 * @returns {string} The resolved description text.
 */
const resolveDescription = (isEarned, earnedDescription, lockedDescription, fallbackDescription, badgeTitle) => {
    if (isEarned && earnedDescription) return earnedDescription;
    if (!isEarned && lockedDescription) return lockedDescription;
    return fallbackDescription || badgeTitle;
};

/**
 * Maps internal badge type keys to emoji fallback icons.
 * Used when no image asset is available or the image fails to load.
 *
 * @param {string} badgeType - The badge_name identifier from the database.
 * @returns {string} An emoji character representing the badge category.
 */
const getFallbackIcon = (badgeType) => {
    switch (badgeType) {
        case 'streak_master': return '🔥';
        case 'problem_crusher': return '🎯';
        case 'daily_champion': return '🏆';
        case 'storyteller': return '📖';
        default: return '🏅';
    }
};

/**
 * Resolves the best available image source for a badge.
 * Priority: database-stored imagePath -> convention-based /badges/{id}.png -> null.
 *
 * @param {string|undefined} imagePath - Direct URL/path stored in the database.
 * @param {string|undefined} badgeId - Badge ID used to construct a convention-based path.
 * @returns {string|null} The resolved image URL, or null if no source is available.
 */
const resolveImageSrc = (imagePath, badgeId) => {
    if (imagePath) return imagePath;
    if (badgeId) return `/badges/${badgeId}.png`;
    return null;
};

/**
 * Returns Tailwind filter classes for locked (unearned) badges.
 * Earned badges receive no additional styling.
 *
 * @param {boolean} isEarned - Whether the badge has been unlocked.
 * @returns {string} Tailwind utility classes for the visual treatment.
 */
const getTierStyling = (isEarned) => {
    if (isEarned) return '';
    return 'grayscale brightness-50 opacity-75';
};

/* ══════════════════════════════════════════════════════════════
 * MAIN COMPONENT
 * ══════════════════════════════════════════════════════════════ */

/**
 * @component BadgeImage
 * @description Renders a single achievement badge with automatic image resolution,
 *              loading skeleton, error fallback, and earned/locked visual states.
 *              Consumed by BadgeCarousel, ProfilePage, and the Dashboard badge notification modal.
 *
 * @param {Object} props
 * @param {string} props.badgeId - Unique badge identifier (used for convention-based image paths).
 * @param {string} [props.imagePath] - Direct image URL from the database (highest priority source).
 * @param {string} props.badgeTitle - Human-readable badge name, used for alt text and tooltips.
 * @param {string} props.badgeType - Internal badge category key (e.g. 'streak_master').
 * @param {string} props.tier - Achievement tier level (bronze/silver/gold/platinum).
 * @param {boolean} [props.isEarned=true] - Whether the user has unlocked this badge.
 * @param {'xs'|'sm'|'md'|'lg'|'xl'} [props.size='md'] - Render size preset.
 * @param {string} [props.className=''] - Additional CSS classes for the outer container.
 * @param {boolean} [props.showFallback=true] - Whether to render an emoji icon when no image is available.
 * @param {string} [props.earnedDescription] - Tooltip text for earned state.
 * @param {string} [props.lockedDescription] - Tooltip text for locked state.
 * @param {string} [props.fallbackDescription='Description not available'] - Generic fallback tooltip.
 *
 * @returns {JSX.Element|null} The rendered badge image, emoji fallback, or null.
 */
const BadgeImage = ({
    badgeId,
    imagePath,
    badgeTitle,
    badgeType,
    tier,
    isEarned = true,
    size = 'md',
    className = '',
    showFallback = true,
    earnedDescription,
    lockedDescription,
    fallbackDescription = 'Description not available'
}) => {
    // State: tracks whether the <img> is still loading or has errored out
    const [imageError, setImageError] = useState(false);
    const [imageLoading, setImageLoading] = useState(true);

    /** Marks the image as successfully loaded (hides the skeleton). */
    const handleImageLoad = () => {
        setImageLoading(false);
    };

    /** Flags an image load failure so the fallback path is triggered. */
    const handleImageError = () => {
        setImageError(true);
        setImageLoading(false);
    };

    // Resolve the tooltip text and image source once per render
    const description = resolveDescription(isEarned, earnedDescription, lockedDescription, fallbackDescription, badgeTitle);
    const imageSrc = resolveImageSrc(imagePath, badgeId);

    /* ── Fallback Path: No image source available or image failed to load ── */
    if (!imageSrc || imageError) {
        if (!showFallback) return null;

        return (
            <div className={`
                ${SIZE_CLASSES[size]} 
                ${className}
                ${isEarned ? '' : 'opacity-50 grayscale'} 
                flex items-center justify-center
                `}
                title={description}>
                <span className="text-lg">{getFallbackIcon(badgeType)}</span>
            </div>
        );
    }

    /* ── Primary Path: Render badge image with loading skeleton ── */
    return (
        <div className={`relative ${SIZE_CLASSES[size]} ${className}`} title={description}>
            {/* Skeleton shimmer — visible while the image is loading */}
            {imageLoading && (
                <div
                    className={`absolute inset-0 bg-zinc-700 rounded-lg animate-pulse${SIZE_CLASSES[size]}`}
                />
            )}

            {/* Badge image — fades in on load, applies locked styling if unearned */}
            <img
                src={imageSrc}
                alt={badgeTitle || 'Badge'}
                className={`
          ${SIZE_CLASSES[size]} 
          object-cover
          ${getTierStyling(isEarned)}
          ${imageLoading ? 'opacity-0' : 'opacity-100'}
          transition-all duration-300
        `}
                onLoad={handleImageLoad}
                onError={handleImageError}
                loading="lazy"
            />
        </div>
    );
};

export default BadgeImage;